import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import {
  getSportPreferences,
  SportPreferencePayload,
  updateSportPreferences,
} from '@/services/sportPreferences';
import FormSection from '@/components/forms/FormSection';
import Select from '@/components/forms/Select';
import Button from '@/components/ui/Button';
import { arenaService, SportCategory } from '@/services/arenaService';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';

// Types
interface SportPreferenceValues {
  level: string;
  categories: string[];
  goals: string[];
}

const initialValues: SportPreferenceValues = {
  level: '',
  categories: [],
  goals: [],
};

const schema = Yup.object({
  level: Yup.string(),
  categories: Yup.array().of(Yup.string()).min(1, 'Pilih minimal satu kategori'),
  goals: Yup.array().of(Yup.string()).min(1, 'Pilih minimal satu tujuan'),
});

interface SportPreferenceTabProps {
  onSubmit?: (values: SportPreferenceValues, tabId: string) => Promise<void>;
}

const GOAL_OPTIONS = ['Cari teman main', 'Cari tim', 'Kompetisi', 'Fun / Health'];

const SKILL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const SportPreferencesTab = ({ onSubmit }: SportPreferenceTabProps) => {
  const [initial, _setInitial] = useState<SportPreferenceValues>(initialValues);
  const [loading, setLoading] = useState(false);
  const [kategoriOptions, setKategoriOptions] = useState<
    { value: string; label: string; icon: string }[]
  >([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!mounted) return;
      setLoading(true);
      try {
        const res = await getSportPreferences();
        if (!mounted) return;
        const data = res.data;
        // backend returns category slugs like ['mini-soccer', 'futsal']
        const mapped: SportPreferenceValues = {
          level: data.level || '',
          categories: data.categories || [],
          goals: data.goals || (data as any).tujuan || [],
        };
        _setInitial(mapped);
      } catch (_err) {
        // ignore load errors for now — developers can hook logger here
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const cats: SportCategory[] = await arenaService.getSportCategories();
      if (!mounted) return;
      const opts = cats.map((c) => ({
        value: c.slug || String(c.id) || c.name.toLowerCase(),
        label: c.name,
        icon: c.icon || 'mdi:run-fast',
      }));
      setKategoriOptions(opts);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Formik
      initialValues={initial}
      enableReinitialize
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          if (onSubmit) {
            const payload: SportPreferencePayload = {
              level: values.level,
              categories: values.categories,
              goals: values.goals,
            };
            await updateSportPreferences(payload as any);
            showToast.success('Preferensi olahraga berhasil diperbarui');
          }
        } catch (err: any) {
          const message =
            err?.response?.data?.message || err?.message || 'Gagal memperbarui preferensi olahraga';
          showToast.error(message);
          helpers.setSubmitting(false);
          throw err;
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form className="space-y-6">
          <FormSection title="Preferensi Olahraga">
            <div className="max-w-md">
              <h3 className="text-base font-medium text-gray-800 mb-3">Olahraga Favorit</h3>
              <Select
                name="level"
                label=""
                placeholder="Pilih skill level"
                options={SKILL_OPTIONS}
              />
              <ErrorMessage name="level" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="col-span-2 md:col-span-2">
                <h3 className="text-base font-medium text-gray-800 mb-3">Olahraga Favorit</h3>
                <div className="mt-3 grid md:grid-cols-3 grid-cols-2 gap-3">
                  {kategoriOptions.map((kategori) => (
                    <label key={kategori.value} className="flex items-center">
                      <Field
                        type="checkbox"
                        name="categories"
                        value={kategori.value}
                        className="w-5 h-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 mr-2"
                      />
                      <Icon icon={`mdi:${kategori.icon}`} className="w-5 h-5 mr-1" />
                      <span className="text-xs text-gray-700">{kategori.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="categories"
                  component="div"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div className="col-span-2 md:col-span-2">
                <h3 className="text-base font-medium text-gray-800 mb-3">Tujuan bergabung</h3>
                <div className="grid grid-cols-1 gap-3">
                  {GOAL_OPTIONS.map((goal) => (
                    <label key={goal} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={values.goals.includes(goal)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFieldValue('goals', [...values.goals, goal]);
                          } else {
                            setFieldValue(
                              'goals',
                              values.goals.filter((g) => g !== goal)
                            );
                          }
                        }}
                        className="w-5 h-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 mr-3"
                      />
                      <span className="text-sm text-gray-700">{goal}</span>
                    </label>
                  ))}
                  <ErrorMessage
                    name="goals"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>
            </div>
          </FormSection>

          <div className="flex justify-start">
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting || loading}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6"
            >
              Save
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SportPreferencesTab;
